import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { gql, useLazyQuery } from "@apollo/client";
import { toast } from "react-toastify";
import useGlobalMutation from "../../../hooks/useGlobalMutation";
import { FormContainer, SelectInput, TextInput } from "../../shared";
import { RegexPattern, clearCache } from "../../../utils";
import {
    VEEVA_INSTANCE_TYPES,
    AEM_ENVIRONMENTS,
    PIAP_ENVIRONMENTS,
    ICC_ENVIRONMENTS,
} from "../../../utils/appList";
import { GET_CONFIG_APP_LIST, GET_VEEVA_INSTANCE_COUNTRY } from "./query";

const IccConfigForm = ({ formData, formType, query }) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        control,
        watch,
    } = useForm({
        mode: "onChange",
        defaultValues: {
            ...formData,
            veevaInstanceType: formData ? formData.veevaInstanceType.veevaInstanceType : "",
        },
    });

    const [configAppList, setConfigAppList] = useState([]);
    const [getConfigAppList, { loading: appLoading }] = useLazyQuery(gql(GET_CONFIG_APP_LIST));

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getConfigAppList();
                const appData = response.data?.allIccAppConfigurations?.nodes;

                if (appData) {
                    const _appList = appData.map((node) => {
                        return { id: node.id, name: node.appName };
                    });
                    setConfigAppList(_appList);
                }
            } catch (error) {
                toast.error(error.message);
            }
        };

        fetchData();
    }, []);

    const veevaInstanceType = watch("veevaInstanceType");
    const [veevaInstanceCountry, setVeevaInstanceCountry] = useState([]);
    const [getVeevaInstanceCountry, { loading: countryLoading }] = useLazyQuery(
        gql(GET_VEEVA_INSTANCE_COUNTRY),
        {
            variables: {
                filter: {
                    veevaInstanceType: {
                        equalTo: veevaInstanceType,
                    },
                },
            },
        }
    );

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getVeevaInstanceCountry();
                const data = response.data?.allVeevaEnvironmentConfigurations?.nodes;

                if (data) {
                    const _appList = data.map((node) => {
                        return { id: node.id, name: node.country };
                    });
                    setVeevaInstanceCountry(_appList);
                }
            } catch (error) {
                toast.error(error.message);
            }
        };

        fetchData();
    }, [veevaInstanceType]);

    const { submitMutation, loading } = useGlobalMutation(query, (cache) => {
        clearCache(cache, "allIccEnvironmentConfigurations");
    });

    const handleSubmission = async (data) => {
        const {
            id,
            aemEnvironment,
            appAid,
            clientId,
            clientSecret,
            iccAppConfigurationId,
            iccEnvironment,
            localCode,
            piapEnvironmentPath,
            redirectUrl,
            veevaConfigId,
            aemHomePageUrl,
            aemErrorPagePath,
        } = data;

        const defaultVariables = {
            iccEnvironmentConfiguration: {
                aemEnvironment,
                appAid,
                clientId,
                clientSecret,
                iccAppConfigurationId,
                iccEnvironment,
                localCode,
                piapEnvironmentPath,
                redirectUrl,
                veevaEnvironmentConfigurationId: veevaConfigId,
                aemHomePageUrl,
                aemErrorPagePath,
            },
        };

        const variables =
            formType === "update"
                ? {
                      id,
                      ...defaultVariables,
                  }
                : {
                      ...defaultVariables,
                  };

        const dataPath =
            formType === "update"
                ? "updateIccEnvironmentConfigurationById.iccEnvironmentConfiguration"
                : "createIccEnvironmentConfiguration.iccEnvironmentConfiguration";

        const isResultNull = await submitMutation(
            variables,
            dataPath,
            `Successfully ${formType === "update" ? "Updated" : "Created"}`
        );
        isResultNull === null && formType !== "update" && reset();
    };

    return (
        <FormContainer
            submitAction={handleSubmit(handleSubmission)}
            loading={loading || appLoading || countryLoading}
            submitButtonText={`${formType === "update" ? "Update" : "Create"} ICC Config`}
            submitBtnSm={formType === "update"}
        >
            <div className="row">
                <div className="col-lg-6">
                    <TextInput
                        id="appAid"
                        type="text"
                        labelText="Application ID"
                        {...register("appAid", {
                            pattern: RegexPattern.nameWithoutSpace,
                            required: true,
                        })}
                        isInvalid={!!errors.appAid}
                        required
                    />
                </div>
                <div className="col-lg-6">
                    <Controller
                        name="iccAppConfigurationId"
                        control={control}
                        defaultValue={
                            formType === "update" ? formData.iccAppConfigurationId : configAppList
                        }
                        render={({ field }) => (
                            <SelectInput
                                {...field}
                                id="iccAppConfigurationId"
                                labelText="ICC Configured App Name"
                                {...register("iccAppConfigurationId", {
                                    required: true,
                                    pattern: RegexPattern.nameWithoutSpace,
                                })}
                                options={configAppList || []}
                                isInvalid={!!errors.iccAppConfigurationId}
                                required
                            />
                        )}
                    />
                </div>
                <div className="col-lg-6">
                    <TextInput
                        id="clientId"
                        type="text"
                        labelText="Client ID"
                        {...register("clientId", {
                            pattern: RegexPattern.noSpace,
                            required: true,
                        })}
                        isInvalid={!!errors.clientId}
                        required
                    />
                </div>
                <div className="col-lg-6">
                    <TextInput
                        id="clientSecret"
                        type="text"
                        labelText="Client Secret"
                        {...register("clientSecret", {
                            pattern: RegexPattern.noSpace,
                            required: true,
                        })}
                        isInvalid={!!errors.clientSecret}
                        required
                    />
                </div>
                <div className="col-lg-6">
                    <TextInput
                        id="localCode"
                        type="text"
                        labelText="Local Code"
                        {...register("localCode", {
                            pattern: RegexPattern.nameWithoutSpace,
                            required: true,
                        })}
                        isInvalid={!!errors.localCode}
                        required
                    />
                </div>
                <div className="col-lg-6">
                    <SelectInput
                        id="piapEnvironmentPath"
                        labelText="PIAP Environment"
                        options={PIAP_ENVIRONMENTS}
                        {...register("piapEnvironmentPath", {
                            required: true,
                        })}
                        isInvalid={!!errors.piapEnvironmentPath}
                        required
                    />
                </div>
                <div className="col-lg-6">
                    <TextInput
                        id="redirectUrl"
                        type="text"
                        labelText="Redirect URL"
                        {...register("redirectUrl", {
                            pattern: RegexPattern.link,
                            required: true,
                        })}
                        isInvalid={!!errors.redirectUrl}
                        required
                    />
                </div>
                <div className="col-lg-6">
                    <SelectInput
                        id="aemEnvironment"
                        labelText="AEM Environment"
                        options={AEM_ENVIRONMENTS}
                        {...register("aemEnvironment", {
                            required: true,
                        })}
                        isInvalid={!!errors.aemEnvironment}
                        required
                    />
                </div>
                <div className="col-lg-6">
                    <SelectInput
                        id="iccEnvironment"
                        labelText="ICC Environment"
                        options={ICC_ENVIRONMENTS}
                        {...register("iccEnvironment", {
                            required: true,
                        })}
                        isInvalid={!!errors.iccEnvironment}
                        required
                    />
                </div>
                <div className="col-lg-6">
                    <SelectInput
                        id="veevaInstanceType"
                        labelText="Veeva Instance Type"
                        options={VEEVA_INSTANCE_TYPES}
                        {...register("veevaInstanceType", {
                            required: true,
                        })}
                        // @ts-expect-error: expected error message
                        // onChange={(e) => handleInstanceChange(e)}
                        isInvalid={!!errors.veevaInstanceType}
                        required
                    />
                </div>

                <div className="col-lg-6">
                    <Controller
                        name="veevaConfigId"
                        control={control}
                        // defaultValue={defaultValues.veevaConfigId}
                        render={({ field }) => (
                            <SelectInput
                                {...field}
                                id="veevaConfigId"
                                labelText="Veeva Instance Country"
                                {...register("veevaConfigId", {
                                    required: true,
                                })}
                                options={veevaInstanceCountry}
                                isInvalid={!!errors.veevaConfigId}
                                required
                            />
                        )}
                    />
                </div>

                <div className="col-lg-6">
                    <TextInput
                        id="aemHomePageUrl"
                        type="text"
                        labelText="AEM Home Page URL"
                        {...register("aemHomePageUrl", {
                            pattern: RegexPattern.link,
                            required: true,
                        })}
                        isInvalid={!!errors.aemHomePageUrl}
                        required
                    />
                </div>

                <div className="col-lg-6">
                    <TextInput
                        id="aemErrorPagePath"
                        type="text"
                        labelText="AEM Error Page Path"
                        {...register("aemErrorPagePath", {
                            pattern: RegexPattern.pagePath,
                            required: true,
                        })}
                        isInvalid={!!errors.aemErrorPagePath}
                        required
                    />
                </div>
            </div>
        </FormContainer>
    );
};

export default IccConfigForm;
